import React, { useState } from 'react';
import Modal from '@mui/material/Modal';
import { Box, IconButton, Typography } from '@mui/material';
import { Button } from 'reactstrap';
import { makeStyles } from '@mui/styles';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import '../../styles/buttons.css';

const useStyles = makeStyles({
    root: {
        position: 'fixed' as 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#000000a8',
        border: '2px solid #000',
        width: "95%",
        maxWidth: "1000px",
        minWidth: "600px",
        maxHeight: "95vh",
        overflow: "auto",
        whiteSpace: "pre-line",
        padding: "20px",
        '@media (max-width: 600px)': {
            width: '95%',
            minWidth: 'auto',
            padding: '15px'
        }
    },
    title: {
        marginTop: "10px",
        fontSize: "32px",
        fontFamily: 'Banschrift',
        color: 'white',
        textAlign: 'center',
        marginBottom: '20px'
    },
    bodyText: {
        padding: '30px',
        color: 'white',
        textAlign: 'left',
        fontFamily: 'Banschrift',
        fontSize: '18px',
        display: "flex",
        flexDirection: "column",
        alignContent: "center",
        justifyContent: "center",
        gap: "15px"
    },
    imagePreview: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        marginTop: '10px'
    },
    imageContainer: {
        position: 'relative',
        width: '150px',
        height: '150px',
        border: '2px solid white',
        borderRadius: '5px',
        overflow: 'hidden'
    },
    image: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    deleteButton: {
        position: 'absolute',
        top: '5px',
        right: '5px',
        backgroundColor: 'rgba(255, 0, 0, 0.7)',
        color: 'white',
        '&:hover': {
            backgroundColor: 'rgba(255, 0, 0, 0.9)',
        }
    }
});

interface ImageData {
    id: string;
    name: string;
    base64: string;
    preview: string;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSave: (images: ImageData[]) => void;
    selectedItem: string;
}

const ImageUploadModal: React.FC<Props> = ({ isOpen, onClose, onSave, selectedItem }) => {
    const styles = useStyles();
    const [images, setImages] = useState<ImageData[]>([]);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return;

        Array.from(files).forEach(file => {
            const reader = new FileReader();

            reader.onload = (event) => {
                if (event.target?.result) {
                    const binaryData = event.target.result as string;
                    const base64String = window.btoa(binaryData);

                    const newImage: ImageData = {
                        id: Date.now().toString() + Math.random().toString(),
                        name: file.name,
                        base64: base64String,
                        preview: `data:${file.type};base64,${base64String}`
                    };

                    setImages(prev => [...prev, newImage]);
                }
            };

            reader.readAsBinaryString(file);
        });

        // Reset input
        e.target.value = '';
    };

    const handleRemoveImage = (id: string) => {
        setImages(prev => prev.filter(img => img.id !== id));
    };

    const handleSave = () => {
        if (images.length === 0) {
            alert("Lisa vähemalt üks pilt");
            return;
        }
        onSave(images);
        setImages([]);
        onClose();
    };

    const handleClose = () => {
        setImages([]);
        onClose();
    };

    return (
        <Modal
            open={isOpen}
            onClose={handleClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box className={styles.root}>
                <Typography className={styles.title} id="modal-modal-title" variant="h6" component="h2">
                    Lisa pilte tootele
                </Typography>
                <Box className={styles.bodyText} id="modal-modal-description">
                    <div style={{ color: '#aaa', fontSize: '16px' }}>
                        Valitud toode: <strong>{selectedItem}</strong>
                    </div>

                    <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                        <label style={{
                            padding: "12px 24px",
                            border: "2px solid white",
                            fontSize: "16px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: "pointer",
                            borderRadius: "44px",
                            gap: "8px",
                            background: "rgba(255, 255, 255, 0.15)",
                            color: "white",
                            transition: "250ms ease-in-out",
                            minWidth: "200px",
                            backdropFilter: "blur(5px)"
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = '#e10008';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                e.currentTarget.style.color = 'white';
                            }}
                        >
                            <AddPhotoAlternateIcon />
                            Lisa pilt galeriist
                            <input
                                style={{ display: 'none' }}
                                title="Add image"
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleFileSelect}
                            />
                        </label>

                        <label style={{
                            padding: "12px 24px",
                            border: "2px solid white",
                            fontSize: "16px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: "pointer",
                            borderRadius: "44px",
                            gap: "8px",
                            background: "rgba(255, 255, 255, 0.15)",
                            color: "white",
                            transition: "250ms ease-in-out",
                            minWidth: "200px",
                            backdropFilter: "blur(5px)"
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = '#e10008';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                                e.currentTarget.style.color = 'white';
                            }}
                        >
                            <CameraAltIcon />
                            Tee pilt kaameraga
                            <input
                                style={{ display: 'none' }}
                                title="Take picture"
                                type="file"
                                accept="image/*"
                                capture="environment"
                                onChange={handleFileSelect}
                            />
                        </label>
                    </div>

                    {images.length > 0 && (
                        <>
                            <div style={{ fontSize: '16px', marginTop: '10px' }}>
                                Lisatud pilte: {images.length}
                            </div>
                            <div className={styles.imagePreview}>
                                {images.map((image) => (
                                    <div key={image.id} className={styles.imageContainer}>
                                        <img src={image.preview} alt={image.name} className={styles.image} />
                                        <IconButton
                                            className={styles.deleteButton}
                                            size="small"
                                            onClick={() => handleRemoveImage(image.id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </Box>

                <div style={{ display: "flex", justifyContent: "center", gap: "15px", margin: "25px", paddingBottom: "15px" }}>
                    <Button
                        className="button-primary"
                        style={{
                            minWidth: "250px",
                            opacity: images.length === 0 ? 0.5 : 1,
                            cursor: images.length === 0 ? 'not-allowed' : 'pointer',
                            background: images.length === 0 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(5px)'
                        }}
                        onClick={handleSave}
                        disabled={images.length === 0}
                    >
                        Salvesta ja saada Directosse
                    </Button>
                    <Button
                        className="button-primary"
                        style={{
                            minWidth: "150px",
                            background: 'rgba(225, 0, 8, 0.2)',
                            backdropFilter: 'blur(5px)'
                        }}
                        onClick={handleClose}
                    >
                        Tühista
                    </Button>
                </div>
            </Box>
        </Modal>
    );
};

export default ImageUploadModal;
